import PropTypes from 'prop-types';
import React from 'react';
import { Formik, Form, Field } from 'formik';
import { Button } from 'primereact/button';
import { useMutation } from '@apollo/react-hooks';
import { Fieldset } from 'primereact/fieldset';
import { injectIntl } from 'react-intl';
// import { Message } from 'primereact/message';
import UPDATE_RESPONDENT_DETAILS from '../mutations';
import buildValidationSchema from '../validation';
import Debug from '../../../../components/Debug';
import { LegacyInputTextComponent, DropdownWithValidation } from '../../../../components/prime-formik';
import { withGrowl } from '../../../../components/GrowlNotifications';
import hashCode from '../../../../utils/StringUtil';


const ariMessages = {
  noAnswerLabel: {
    id: 'ari.respondent.edit.dialog.noAnswer',
    defaultMessage: 'No answer',
  },
  updateLabel: {
    id: 'ari.respondent.edit.dialog.updateLabel',
    defaultMessage: 'Update',
  },
  globalFieldsetLegend: {
    id: 'ari.respondent.display.global.header',
    defaultMessage: 'Global',
  },
  localFieldsetLegend: {
    id: 'ari.respondent.display.local.header',
    defaultMessage: 'Local',
  },
  nothingToUpdate: {
    id: 'ari.respondent.edit.no.update',
    defaultMessage: 'Nothing to update',
  },
  updateSuccessSummary: {
    id: 'ari.respondent.update.success.summary',
    defaultMessage: 'Success',
  },
  updateSuccessDetail: {
    id: 'ari.respondent.update.success.detail',
    defaultMessage: 'Answers were successfully updated.',
  },
  updateFailSummary: {
    id: 'ari.respondent.update.fail.summary',
    defaultMessage: 'Error updating',
  },
  updateFailDetail: {
    id: 'ari.respondent.update.fail.detail',
    defaultMessage: 'There was an error updating answers.',
  },
};

const UpdateCurrentAriAnswers = ({
  currentAccount, respondent, intl: { formatMessage }, setIsVisible, growl,
}) => {
  const [updateRespondentDetails] = useMutation(UPDATE_RESPONDENT_DETAILS,
    { refetchQueries: ['getAccount', 'getRespondent'] });

  const validationSchema = buildValidationSchema(formatMessage, respondent);

  const initialValues = {};

  respondent.globalAriFields.forEach((gf) => {
    initialValues[`global_${gf.id}_${hashCode(gf.question)}`] = gf.answer || '';
  });

  respondent.localAriFields.forEach((lf) => {
    initialValues[`local_${lf.id}_${hashCode(lf.question)}`] = lf.answer || '';
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(true);
        const input = {
          respondentId: respondent.id,
          firstName: respondent.firstName,
          lastName: respondent.lastName,
          email: respondent.email,
          notes: respondent.notes,
          gender: respondent.gender,
          locale: respondent.locale,
          type: respondent.type,
          accountId: currentAccount,
          ariFields: [
            ...respondent.globalAriFields.map(gf => ({
              ariFieldId: gf.id,
              question: gf.question,
              mandatory: gf.mandatory,
              answer: values[`global_${gf.id}_${hashCode(gf.question)}`],
              ariFieldType: 'GLOBAL',
              ariInputType: gf.inputType,
              dropdownAnswers: gf.dropdownAnswers,
            })),
            ...respondent.localAriFields.map(lf => ({
              ariFieldId: lf.id,
              question: lf.question,
              mandatory: lf.mandatory,
              answer: values[`local_${lf.id}_${hashCode(lf.question)}`],
              ariFieldType: 'LOCAL',
              ariInputType: lf.inputType,
              dropdownAnswers: lf.dropdownAnswers,
            })),
          ],
        };

        updateRespondentDetails({ variables: { input } })
          .then(() => {
            growl.show({
              severity: 'success',
              sticky: false,
              summary: formatMessage(ariMessages.updateSuccessSummary),
              detail: formatMessage(ariMessages.updateSuccessDetail),
            });

            setSubmitting(false);
            setIsVisible(false);
          })
          .catch(() => {
            growl.show({
              severity: 'error',
              sticky: true,
              summary: formatMessage(ariMessages.updateFailSummary),
              detail: formatMessage(ariMessages.updateFailDetail),
            });
          });
      }}
    >
      {({ isSubmitting }) => (
        <Form>
          <div className="p-grid p-justify-center" style={{ padding: '5px', marginTop: '10px' }}>
            <Fieldset
              className="p-col"
              legend={formatMessage(ariMessages.globalFieldsetLegend)}
              style={{ margin: '5px' }}
            >
              {respondent.globalAriFields.length === 0 ? (
                <p style={{ textAlign: 'center' }}>
                  {formatMessage(ariMessages.nothingToUpdate)}
                </p>
              )
                : (respondent.globalAriFields.map((gf, index) => {
                  const dropdownOptions = [
                    {
                      key: 'No answer',
                      label: formatMessage(ariMessages.noAnswerLabel),
                      value: '',
                    },
                    ...gf.dropdownAnswers.map(answer => ({
                      key: answer,
                      label: answer,
                      value: answer,
                    })),
                  ];
                  return (
                    <div className="p-col-12" style={{ textAlign: 'center' }} key={gf.id}>
                      <div className="form-group" key={gf.question} style={{ marginBottom: '5px' }}>
                        <label className="field-label" htmlFor={`global${index + 1}`} style={{ fontStyle: 'italic' }}>
                          {`${gf.question}${gf.mandatory ? '*' : ''}`}
                        </label>
                      </div>
                      {gf.inputType === 'TEXT'
                        && (
                          <Field
                            id={`global${index + 1}`}
                            name={`global_${gf.id}_${hashCode(gf.question)}`}
                            component={LegacyInputTextComponent}
                          />
                        )}
                      {gf.inputType === 'NUMBER'
                        && (
                          <Field
                            id={`global${index + 1}`}
                            name={`global_${gf.id}_${hashCode(gf.question)}`}
                            component={LegacyInputTextComponent}
                          />
                        )}
                      {gf.inputType === 'DROPDOWN'
                        && (
                          <Field
                            id={`global${index + 1}`}
                            name={`global_${gf.id}_${hashCode(gf.question)}`}
                            component={DropdownWithValidation}
                            options={dropdownOptions}
                          />
                        )}
                    </div>
                  );
                }))}
            </Fieldset>
            <Fieldset
              className="p-col"
              legend={formatMessage(ariMessages.localFieldsetLegend)}
              style={{ margin: '5px' }}
            >
              {respondent.localAriFields.length === 0
                ? (
                  <p style={{ textAlign: 'center' }}>
                    {formatMessage(ariMessages.nothingToUpdate)}
                  </p>
                )
                : (respondent.localAriFields.map((lf, index) => {
                  const dropdownOptions = [
                    {
                      key: 'No answer',
                      label: formatMessage(ariMessages.noAnswerLabel),
                      value: '',
                    },
                    ...lf.dropdownAnswers.map(answer => ({
                      key: answer,
                      label: answer,
                      value: answer,
                    })),
                  ];

                  return (
                    <div className="p-col-12" style={{ textAlign: 'center' }} key={lf.id}>
                      <div className="form-group" key={lf.question} style={{ marginBottom: '5px' }}>
                        <label className="field-label" htmlFor={`local${index + 1}`} style={{ fontStyle: 'italic' }}>
                          {`${lf.question}${lf.mandatory ? '*' : ''}`}
                        </label>
                      </div>
                      {lf.inputType === 'TEXT'
                        && (
                          <Field
                            id={`local${index + 1}`}
                            name={`local_${lf.id}_${hashCode(lf.question)}`}
                            component={LegacyInputTextComponent}
                          />
                        )}
                      {lf.inputType === 'NUMBER'
                        && (
                          <Field
                            id={`local${index + 1}`}
                            name={`local_${lf.id}_${hashCode(lf.question)}`}
                            component={LegacyInputTextComponent}
                          />
                        )}
                      {lf.inputType === 'DROPDOWN'
                        && (
                          <Field
                            id={`local${index + 1}`}
                            name={`local_${lf.id}_${hashCode(lf.question)}`}
                            component={DropdownWithValidation}
                            options={dropdownOptions}
                          />
                        )}
                    </div>
                  );
                }))}
            </Fieldset>
            <div className="p-col-12" style={{ textAlign: 'center' }}>
              <Button
                label={formatMessage(ariMessages.updateLabel)}
                type="submit"
                disabled={isSubmitting}
              />
            </div>
          </div>
          <Debug />
        </Form>
      )}
    </Formik>
  );
};

UpdateCurrentAriAnswers.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  respondent: PropTypes.objectOf(PropTypes.any).isRequired,
  intl: PropTypes.objectOf(PropTypes.any).isRequired,
  setIsVisible: PropTypes.func.isRequired,
  growl: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default injectIntl(withGrowl(UpdateCurrentAriAnswers));
